


import { Keymap } from "../models/keymap";
import { GamepadAxisEvent, GamepadButtonEvent } from "./gamepadEvents.js";
import { MemoryHelper } from "./memory-helper.js";
import { CharNumber } from "../models/charnumber";
import { MouseButton } from "../models/mousebutton.js";
import { GamepadButton } from "../models/gamepadbutton.js";
import { TouchPhase } from "../models/touchphase.js";
import { TouchFlags } from "../models/touchflags.js";

export class FourCC {
  private _code: number;

  constructor(a: string, b: string, c: string, d: string) {
    this._code = (a.charCodeAt(0) << 24) | (b.charCodeAt(0) << 16) | (c.charCodeAt(0) << 8) | d.charCodeAt(0);
  }

  toInt32(): number {
    return this._code;
  }
}

export class InputDevice {
  name: string;
  layout: string;
  deviceId: number;
  usages: string[] | null;
  description: Record<string, any>;
  protected _inputState: IInputState | null;

  constructor(name: string, layout: string, deviceId: number, usages: string[] | null, description: Record<string, any>) {
    this.name = name;
    this.layout = layout;
    this.deviceId = deviceId;
    this.usages = usages;
    this.description = description;
    this._inputState = null;
  }

  updateState(state: IInputState): void {
    this._inputState = state;
  }

  queueEvent(event: Event, ...args: any[]): void {
    throw new Error(`Please implement this method. event: ${event}`);
  }

  get currentState(): IInputState | null {
    return this._inputState;
  }
}

export class Mouse extends InputDevice {
  override queueEvent(event: MouseEvent | WheelEvent): void {
    this.updateState(new MouseState(event));
  }
}

export class Keyboard extends InputDevice {

  override _inputState: KeyboardState | null = null;

  override get currentState(): KeyboardState | null {
    return this._inputState;
  }

  override updateState(state: KeyboardState): void {
    this._inputState = state;
  }

  static get keycount(): number {
    return 110;
  }

  override queueEvent(event: KeyboardEvent): void {
    this.updateState(new KeyboardState(event, this.currentState));
  }
}

export class Touchscreen extends InputDevice {
  override queueEvent(event: TouchEvent, time: number): void {
    this.updateState(new TouchscreenState(event, this.currentState as TouchscreenState, time));
  }
}

export class Gamepad extends InputDevice {
  override queueEvent(event: GamepadButtonEvent | GamepadAxisEvent): void {
    this.updateState(new GamepadState(event));
  }
}

export class InputEvent {
  static get invalidEventId(): number {
    return 0;
  }

  static get size(): number {
    return 20;
  }

  type: number;
  sizeInBytes: number;
  deviceId: number;
  time: number;
  eventId: number;

  constructor(type: number, sizeInBytes: number, deviceId: number, time: number) {
    this.type = type;
    this.sizeInBytes = sizeInBytes;
    this.deviceId = deviceId;
    this.time = time;
    this.eventId = InputEvent.invalidEventId;
  }

  get buffer(): ArrayBuffer {
    const _buffer = new ArrayBuffer(InputEvent.size);
    const view = new DataView(_buffer);
    view.setInt32(0, this.type, true);
    view.setInt16(4, this.sizeInBytes, true);
    view.setInt16(6, this.deviceId, true);
    view.setFloat64(8, this.time, true);
    view.setInt16(16, this.sizeInBytes, true);
    return _buffer;
  }
}

export abstract class IInputState {
  abstract get buffer(): ArrayBuffer;
  abstract get format(): number;
}

export class MouseState extends IInputState {
  static get size(): number {
    return 30;
  }

  static get format(): number {
    return new FourCC('M', 'O', 'U', 'S').toInt32();
  }

  position: number[];
  delta: number[];
  scroll: number[];
  buttons: ArrayBuffer;
  displayIndex!: number;
  clickCount!: number;

  constructor(event: MouseEvent | WheelEvent) {
    super();
    this.position = [event.clientX, event.clientY];
    this.delta = [event.movementX, -event.movementY];
    const wheelEvent = event as WheelEvent;
    this.scroll = event.type === 'wheel' ? [wheelEvent.deltaX, wheelEvent.deltaY] : [0, 0];
    this.buttons = new ArrayBuffer(2);

    const left = (event.buttons & (1 << 0)) !== 0;
    const right = (event.buttons & (1 << 1)) !== 0;
    const middle = (event.buttons & (1 << 2)) !== 0;
    const back = (event.buttons & (1 << 3)) !== 0;
    const forward = (event.buttons & (1 << 4)) !== 0;

    MemoryHelper.writeSingleBit(this.buttons, MouseButton.Left, left);
    MemoryHelper.writeSingleBit(this.buttons, MouseButton.Right, right);
    MemoryHelper.writeSingleBit(this.buttons, MouseButton.Middle, middle);
    MemoryHelper.writeSingleBit(this.buttons, MouseButton.Forward, forward);
    MemoryHelper.writeSingleBit(this.buttons, MouseButton.Back, back);
  }

  get buffer(): ArrayBuffer {
    const size = MouseState.size;
    const buttons = new Uint16Array(this.buttons)[0];
    const _buffer = new ArrayBuffer(size);
    const view = new DataView(_buffer);
    view.setFloat32(0, this.position[0], true);
    view.setFloat32(4, this.position[1], true);
    view.setFloat32(8, this.delta[0], true);
    view.setFloat32(12, this.delta[1], true);
    view.setFloat32(16, this.scroll[0], true);
    view.setFloat32(20, this.scroll[1], true);
    view.setUint16(24, buttons, true);
    view.setUint16(26, this.displayIndex, true);
    view.setUint16(28, this.clickCount, true);
    return _buffer;
  }

  get format(): number {
    return MouseState.format;
  }
}

export class KeyboardState extends IInputState {
  static get sizeInBits(): number {
    return Keyboard.keycount;
  }

  static get sizeInBytes(): number {
    return (KeyboardState.sizeInBits + 7) >> 3;
  }

  static get format(): number {
    return new FourCC('K', 'E', 'Y', 'S').toInt32();
  }

  keys: ArrayBuffer;

  constructor(event: KeyboardEvent, state: KeyboardState | null) {
    super();
    this.keys = state?.keys ?? new ArrayBuffer(KeyboardState.sizeInBytes);

    let value: boolean;
    switch (event.type) {
      case 'keydown':
        value = true;
        break;
      case 'keyup':
        value = false;
        break;
      default:
        throw new Error(`unknown event type ${event.type}`);
    }

    const key = Keymap[event.code];
    MemoryHelper.writeSingleBit(this.keys, key, value);
  }

  get buffer(): ArrayBuffer {
    return this.keys;
  }

  get format(): number {
    return KeyboardState.format;
  }
}

export class TouchState {
  static _currentTouchId?: number;
static _prevTouches: any[];
  static get format(): number {
    return new FourCC('T', 'O', 'U', 'C').toInt32();
  }

  static get size(): number {
    return 56;
  }

  static incrementTouchId(): number {
    if (TouchState._currentTouchId === undefined) {
      TouchState._currentTouchId = 0;
    }
    return ++TouchState._currentTouchId;
  }

  static prevTouches(): TouchState[] {
    if (TouchState._prevTouches === undefined) {
      TouchState._prevTouches = new Array(10);
    }
    return TouchState._prevTouches;
  }

  touchId: number;
  position: number[];
  delta: number[];
  pressure: number;
  radius: number[];
  phaseId: number;
  tapCount: number;
  displayIndex: number;
  flags: number;
  padding: number;
  startTime: number;
  startPosition: number[];

  constructor(
    touchId: number,
    prevState: TouchState | null,
    position: number[] | null,
    pressure: number,
    radius: number[] | null,
    phaseId: number,
    time: number
  ) {
    this.touchId = touchId;
    this.position = position ? position.slice() : [];
    this.delta = phaseId === TouchPhase.Moved ? [this.position[0] - prevState!.position[0], this.position[1] - prevState!.position[1]] : [0, 0];
    this.pressure = pressure;
    this.radius = radius ? radius.slice() : [];
    this.phaseId = phaseId;
    this.tapCount = 0;
    this.displayIndex = 0;
    this.flags = 0;
    this.padding = 0;
    this.startTime = phaseId === TouchPhase.Began ? time : prevState!.startTime;
    this.startPosition = phaseId === TouchPhase.Began ? this.position.slice() : prevState!.startPosition.slice();
  }

  copy(): TouchState {
    const state = new TouchState(this.touchId, null, this.position, this.pressure, this.radius, this.phaseId, this.startTime);
    state.delta = this.delta.slice();
    state.tapCount = this.tapCount;
    state.displayIndex = this.displayIndex;
    state.flags = this.flags;
    state.padding = this.padding;
    state.startPosition = this.startPosition.slice();
    return state;
  }
}

export class TouchscreenState extends IInputState {
  static get format(): number {
    return TouchState.format;
  }

  primaryTouchId: number;
  primaryTouch: TouchState | null;
  touches: TouchState[];
  time: number;

  constructor(event: TouchEvent, prevState: TouchscreenState | null, time: number) {
    super();
    this.time = time;
    this.primaryTouchId = prevState?.primaryTouchId ?? 0;
    this.primaryTouch = prevState?.primaryTouch ? prevState.primaryTouch.copy() : null;
    this.touches = prevState?.touches.map((touch) => touch.copy()) ?? [];
    this.updateTouches(event);
  }

  updateTouches(event: TouchEvent): void {
    const { changedTouches, type } = event;

    for (let i = 0; i < changedTouches.length; ++i) {
      const touch = changedTouches[i];
      let touchState: TouchState | null = null;
      let index = 0;

      switch (type) {
        case 'touchstart':
          if (this.touches.length < 10) {
            touchState = new TouchState(
              TouchState.incrementTouchId(),
              null,
              [touch.clientX, touch.clientY],
              touch.force,
              [touch.radiusX, touch.radiusY],
              TouchPhase.Began,
              this.time
            );
            this.touches.push(touchState);
            this.primaryTouch = touchState;
          }
          break;
        case 'touchmove':
          index = this.touches.findIndex((state) => state.touchId === touch.identifier);
          touchState = new TouchState(
            touch.identifier,
            this.touches[index],
            [touch.clientX, touch.clientY],
            touch.force,
            [touch.radiusX, touch.radiusY],
            TouchPhase.Moved,
            this.time
          );
          this.touches[index] = touchState;
          break;
        case 'touchend':
        case 'touchcancel':
          index = this.touches.findIndex((state) => state.touchId === touch.identifier);
          touchState = new TouchState(
            touch.identifier,
            this.touches[index],
            null,
            0,
            null,
            type === 'touchend' ? TouchPhase.Ended : TouchPhase.Canceled,
            this.time
          );
          this.touches[index] = touchState;
          break;
      }
    }

    this.touches = this.touches.filter((touch) => touch.phaseId !== TouchPhase.Ended && touch.phaseId !== TouchPhase.Canceled);
  }

  get buffer(): ArrayBuffer {
    const _buffer = new ArrayBuffer(TouchState.size * this.touches.length);
    const view = new DataView(_buffer);
    let offset = 0;

    for (const touch of this.touches) {
      const localOffset = offset * TouchState.size;
      view.setUint16(localOffset + 0, touch.touchId, true);
      view.setFloat32(localOffset + 4, touch.position[0], true);
      view.setFloat32(localOffset + 8, touch.position[1], true);
      view.setFloat32(localOffset + 12, touch.delta[0], true);
      view.setFloat32(localOffset + 16, touch.delta[1], true);
      view.setFloat32(localOffset + 20, touch.pressure, true);
      view.setFloat32(localOffset + 24, touch.radius[0], true);
      view.setFloat32(localOffset + 28, touch.radius[1], true);
      view.setUint16(localOffset + 32, touch.phaseId, true);
      view.setUint16(localOffset + 34, touch.tapCount, true);
      view.setUint16(localOffset + 36, touch.displayIndex, true);
      view.setUint16(localOffset + 38, touch.flags, true);
      view.setUint16(localOffset + 40, touch.padding, true);
      view.setFloat64(localOffset + 44, touch.startTime, true);
      view.setFloat32(localOffset + 52, touch.startPosition[0], true);
      view.setFloat32(localOffset + 56, touch.startPosition[1], true);
      offset++;
    }

    return _buffer;
  }

  get format(): number {
    return TouchscreenState.format;
  }
}

export class GamepadState extends IInputState {
  static get format(): number {
    return new FourCC('G', 'P', 'A', 'D').toInt32();
  }

  static get sizeInBits(): number {
    return 32;
  }

  static get sizeInBytes(): number {
    return 32;
  }

  buttonState: ArrayBuffer;

  constructor(event: GamepadButtonEvent | GamepadAxisEvent) {
    super();
    this.buttonState = new ArrayBuffer(GamepadState.sizeInBytes);
    let button = event instanceof GamepadButtonEvent ? GamepadButton[event.button] : -1;

    if (button !== -1) {
      MemoryHelper.writeSingleBit(this.buttonState, button, event.pressed);
    }
  }

  get buffer(): ArrayBuffer {
    return this.buttonState;
  }

  get format(): number {
    return GamepadState.format;
  }
}

export class TextEvent {

    baseEvent!: InputEvent;
    character!: number;

    static get format() { return new FourCC('T', 'E', 'X', 'T').toInt32(); }
  
    static create(deviceId: number, event: KeyboardEvent, time: number) {
      const eventSize = InputEvent.size + MemoryHelper.sizeOfInt;
  
      let textEvent = new TextEvent();
      textEvent.baseEvent = new InputEvent(TextEvent.format, eventSize, deviceId, time);
      textEvent.character = CharNumber[event.key];
      return textEvent;
    }
  
    /**
     * @returns {ArrayBuffer}
     */
    get buffer() {
      const size = InputEvent.size + MemoryHelper.sizeOfInt;
      let _buffer = new ArrayBuffer(size);
      let arrayView = new Uint8Array(_buffer);
      let dataView = new DataView(_buffer);
      arrayView.set(new Uint8Array(this.baseEvent.buffer), 0);
      dataView.setInt32(InputEvent.size, this.character, true);
      return _buffer;
    }
  }
  
  export class StateEvent {
    static get format() { return new FourCC('S', 'T', 'A', 'T').toInt32(); }
  
    static from(device: any, time: number) {
      return StateEvent.fromState(device.currentState, device.deviceId, time);
    }

    baseEvent!: InputEvent;
    stateFormat!: number;
    stateData!: ArrayBuffer;
  
    /**
     *
     * @param {IInputState} state
     * @param {Number} deviceId
     * @param {Number} time
     */
    static fromState(state: IInputState, deviceId: number, time: number) {
      const stateData = state.buffer;
      const stateSize = stateData.byteLength;
      const eventSize = InputEvent.size + MemoryHelper.sizeOfInt + stateSize;
  
      let stateEvent = new StateEvent();
      stateEvent.baseEvent = new InputEvent(StateEvent.format, eventSize, deviceId, time);
      stateEvent.stateFormat = state.format;
      stateEvent.stateData = stateData;
      return stateEvent;
    }
  
    /**
     * @returns {ArrayBuffer}
     */
    get buffer() {
      const stateSize = this.stateData.byteLength;
      const size = InputEvent.size + MemoryHelper.sizeOfInt + stateSize;
      let _buffer = new ArrayBuffer(size);
      let uint8View = new Uint8Array(_buffer);
      let dataView = new DataView(_buffer);
      uint8View.set(new Uint8Array(this.baseEvent.buffer), 0);
      dataView.setInt32(InputEvent.size, this.stateFormat, true);
      uint8View.set(new Uint8Array(this.stateData), InputEvent.size+MemoryHelper.sizeOfInt);
      return _buffer;
    }
  }
